import { useLayoutEffect, useRef } from 'react';

import type { ContactInfo } from '@/features/appointments/utils/ContactInfo';
import useIdentityStore from '@/store/IdentityStore';
import type { UserInfo } from '@/types/AceIdentity';
import useResolvedZipCode from './useResolvedZipCode';

interface UseContactInfoPrefillParams {
  formValues: ContactInfo;
  updateField: (contactInfo: Partial<ContactInfo>) => void;
}

const isBlank = (value?: string) => !value?.trim();

const isValidEmail = (value?: string | null): boolean =>
  Boolean(value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()));

const resolveEmail = (userInfo?: UserInfo) => {
  const email = userInfo?.email?.trim();
  if (isValidEmail(email)) {
    return email;
  }

  const preferredUsername = userInfo?.preferred_username?.trim();
  if (isValidEmail(preferredUsername)) {
    return preferredUsername;
  }

  return '';
};

/**
 * Prefills empty first name, last name, email, and ZIP from the authenticated
 * profile and club zip resolution. Each field is written once so a later
 * edit or clear is not overwritten.
 */
const useContactInfoPrefill = ({
  formValues,
  updateField,
}: UseContactInfoPrefillParams) => {
  const userInfo = useIdentityStore((state) => state.userInfo);
  const resolvedZipCode = useResolvedZipCode();

  const givenName = userInfo?.given_name?.trim() ?? '';
  const familyName = userInfo?.family_name?.trim() ?? '';
  const email = resolveEmail(userInfo);
  const zipCode = resolvedZipCode.trim();

  const hasPrefilledFirstName = useRef(false);
  const hasPrefilledLastName = useRef(false);
  const hasPrefilledEmail = useRef(false);
  const hasPrefilledZipCode = useRef(false);

  useLayoutEffect(() => {
    const fieldsToUpdate: Record<string, string> = {};

    if (!isBlank(formValues.firstName)) {
      hasPrefilledFirstName.current = true;
    } else if (!hasPrefilledFirstName.current && givenName) {
      fieldsToUpdate.firstName = givenName;
      hasPrefilledFirstName.current = true;
    }

    if (!isBlank(formValues.lastName)) {
      hasPrefilledLastName.current = true;
    } else if (!hasPrefilledLastName.current && familyName) {
      fieldsToUpdate.lastName = familyName;
      hasPrefilledLastName.current = true;
    }

    if (!isBlank(formValues.email)) {
      hasPrefilledEmail.current = true;
    } else if (!hasPrefilledEmail.current && email) {
      fieldsToUpdate.email = email;
      hasPrefilledEmail.current = true;
    }

    if (!isBlank(formValues.zipCode)) {
      hasPrefilledZipCode.current = true;
    } else if (!hasPrefilledZipCode.current && zipCode) {
      fieldsToUpdate.zipCode = zipCode;
      hasPrefilledZipCode.current = true;
    }

    if (Object.keys(fieldsToUpdate).length > 0) {
      updateField(fieldsToUpdate);
    }
  }, [
    givenName,
    familyName,
    email,
    zipCode,
    formValues.firstName,
    formValues.lastName,
    formValues.email,
    formValues.zipCode,
    updateField,
  ]);
};

export default useContactInfoPrefill;
